import { Flex, Icon } from "@chakra-ui/react";
import { MdUndo } from "react-icons/md";
import { useFormContext } from "react-hook-form";
import { InvoiceFormData } from "@/types";
import { useQuery } from "@tanstack/react-query";
import { getOcrResponseQueryOptions } from "@/api/queryOptions/invoiceAPIQueryOptions";
import { Trans, useTranslation } from "react-i18next";
import { useCallback } from "react";
import { InvoiceFromOcrDto } from "@/api/invoiceApi";
import { Alert, BodyShort, Badge } from "@digital-services-gard-as/anchor-react";
import { useParams } from "react-router-dom";

export const PreFilledBanner = ({ ocrResponse }: { ocrResponse?: InvoiceFromOcrDto }) => {
    if (!hasAnyOcrValues(ocrResponse)) {
        return null;
    }

    return (
        <Alert variant="info" id="prefilledBanner" contentMaxWidth={false}>
            <BodyShort>
                <Trans i18nKey="invoices:createInvoice.prefilledBanner" />
            </BodyShort>
        </Alert>
    );
};

type PreFilledBadgeProps = {
    fieldName: keyof InvoiceFormData;
    isDisabled?: boolean;
    customCompareValue?: string;
};

export const PreFilledBadges = ({ fieldName, customCompareValue, isDisabled }: PreFilledBadgeProps) => {
    const { invoiceId = "" } = useParams<{ invoiceId: string }>();

    const { watch, setValue } = useFormContext<InvoiceFormData>();
    const docId = watch("invoiceAttachment.docId") || "";
    const fieldValue = watch(fieldName) as string | undefined;

    const { data: ocrResponse } = useQuery({
        ...getOcrResponseQueryOptions(invoiceId, docId),
        staleTime: Infinity
    });

    const rawOcrValue =
        ocrResponse && fieldName in ocrResponse ? ocrResponse[fieldName as keyof InvoiceFromOcrDto] : undefined;

    const ocrValue: string | undefined = hasValidOcrValue(rawOcrValue) ? String(rawOcrValue) : undefined;

    const isFieldEdited = Boolean(ocrValue) && fieldValue !== (customCompareValue ?? ocrValue);

    const handleUndo = useCallback(() => {
        if (ocrValue === undefined) {
            return;
        }

        setValue(fieldName, customCompareValue ?? ocrValue, {
            shouldDirty: true,
            shouldTouch: true
        });
    }, [fieldName, ocrValue, customCompareValue, setValue]);

    if (isDisabled || ocrValue === undefined) {
        return null;
    }

    if (isFieldEdited) {
        return <ModifiedBadge handleUndo={handleUndo} />;
    }

    return <PrefilledBadge />;
};

export const PrefilledBadge = () => {
    const { t } = useTranslation(["invoices"]);

    return (
        <Badge variant="info" style={{ maxWidth: "fit-content", whiteSpace: "nowrap" }} data-testid="prefilled-badge">
            {t("invoices:preFilledBadge")}
        </Badge>
    );
};

export const ModifiedBadge = ({ handleUndo }: { handleUndo: () => void }) => {
    const { t } = useTranslation(["invoices"]);

    return (
        <Flex gap={2} alignItems="center">
            <Badge variant="warning" data-testid="modifiedBadge">
                {t("invoices:modifiedBadge")}
            </Badge>
            {/*eslint-disable-next-line no-magic-numbers*/}
            <Icon cursor="pointer" _hover={{ opacity: 0.7 }} aria-label={t("invoices:undoChangesBtn")} asChild>
                <MdUndo onClick={handleUndo} />
            </Icon>
        </Flex>
    );
};

const hasAnyOcrValues = (ocrResponse: Record<string, unknown> | undefined): boolean => {
    if (!ocrResponse) {
        return false;
    }

    return Object.entries(ocrResponse).some(([key, value]) => {
        // Skip metadata fields
        if (excludedOcrFields.has(key)) {
            return false;
        }
        return hasValidOcrValue(value);
    });
};
const hasValidOcrValue = (value: unknown): boolean => value != null && value !== "" && value !== 0;

// Fields that should be excluded from "has OCR data" check (metadata fields)
const excludedOcrFields = new Set([
    "id",
    "companyCrmId",
    "claimsRef",
    "subClaimsRef",
    "accountId",
    "clientName",
    "vesselName",
    "incidentDate",
    "clientVat",
    "vatNumber",
    "totalCosts"
]);
